import { ProjectStatusesHelper } from '@/helpers/project-statuses.helper';
import apiClient from '@/services/api-client';
/**
 * @typedef {import('@/models/office.interface').Office} Office
 */
/**
 * @template S, R
 * @typedef {import('vuex').ActionTree<S, R>} ActionTree<S, R>
 */
/**
 * @template S
 * @typedef {import('vuex').MutationTree<S>} MutationTree<S>
 */
/**
 * @template S, R
 * @typedef {import('vuex').GetterTree<S, R>} GetterTree<S, R>
 */
/**
 * @template S, R
 * @typedef {import('vuex').Module<S, R>} Module<S, R>
 */
/**
 * @typedef {import('@/store/root.state').RootState} RootState
 */

/**
 * @typedef {Object} ProjectsFilters
 * @property {string[]} offices
 * @property {string[]} divisions
 * @property {string[]} types
 * @property {string[] | null} statuses
 * @property {string} projectName
 */

/**
 * @typedef {Object} FilteredProjectList
 * @property {string} client_id
 * @property {string} client_name
 * @property {string | null} end_date
 * @property {string} id
 * @property {Office[]} office
 * @property {string[]} office_ids
 * @property {string} office_name
 * @property {string} start_date
 * @property {string} status
 * @property {string} title
 * @property {string} type
 */

/**
 * @typedef {Object} FilterProjectsState
 * @property {ProjectsFilters} filters
 * @property {FilteredProjectList[]} projectList
 * @property {FilteredProjectList[]} allProjectsForOptions
 */

/** @returns {FilterProjectsState} */
const getInitialState = () => ({
  filters: {
    offices: [],
    divisions: [],
    types: [],
    statuses: null,
    projectName: '',
  },
  projectList: [],
  allProjectsForOptions: [],
});

/** @type {FilterProjectsState} */
const state = getInitialState();

/** @type {GetterTree<FilterProjectsState, RootState>} */
const getters = {
  projectNameFilter: (state) => state.filters.projectName,
  projectOfficeFilter: (state) => state.filters.offices,
  projectDivisionFilter: (state) => state.filters.divisions,
  projectTypeFilter: (state) => state.filters.types,
  projectStatusFilter: ({ filters: { statuses } }, { projectStatusFilterOptions }) => {
    if (statuses || !projectStatusFilterOptions.length) return statuses || [];

    return ProjectStatusesHelper.filterDefaultStatuses(projectStatusFilterOptions)
      .map((projectStatus) => projectStatus.title);
  },
  projectTypeFilterOptions: (state) => {
    const projectTypes = state.allProjectsForOptions.map((project) => (project.type));
    const uniqueProjectTypes = Array.from(new Set(projectTypes));
    const projectTypeFilterOptions = uniqueProjectTypes.map((project) => ({ id: project, title: project }));

    return projectTypeFilterOptions;
  },
  projectStatusFilterOptions: (state) => {
    const projectStatuses = state.allProjectsForOptions
      .map((project) => (project.status))
      .filter((status) => status); // filter added because I get a null status
    const uniqueProjectStatuses = Array.from(new Set(projectStatuses));
    const projectStatusFilterOptions = uniqueProjectStatuses.map((project) => ({ id: project, title: project }));

    return projectStatusFilterOptions;
  },
  projectsFiltered: (state) => state.projectList,
  projectList: (state) => state.projectList,
  allProjectsForOptions: (state) => state.allProjectsForOptions,
};

/** @type {ActionTree<FilterProjectsState, RootState>} */
const actions = {
  setNameFilter: ({ commit, dispatch }, projectNameFilter) => {
    commit('setNameFilter', projectNameFilter);
    dispatch('getProjectList');
  },
  setOfficesFilter: ({ commit, dispatch }, officesFilter) => {
    commit('setOfficesFilter', officesFilter);
    dispatch('getProjectList');
  },
  setDivisionsFilter: ({ commit, dispatch }, divisionsFilter) => {
    commit('setDivisionsFilter', divisionsFilter);
    dispatch('getProjectList');
  },
  setTypesFilter: ({ commit, dispatch }, typesFilter) => {
    commit('setTypesFilter', typesFilter);
    dispatch('getProjectList');
  },
  setStatusesFilter: ({ commit, dispatch }, statusesFilter) => {
    commit('setStatusesFilter', statusesFilter);
    dispatch('getProjectList');
  },

  getProjectList: async ({ state, commit, getters }) => {
    const { filters } = state;
    const statusFilter = getters.projectStatusFilter || [];
    const filterParams = {
      title_search: filters.projectName || undefined,
      office_ids: filters.offices.length > 0 ? filters.offices : undefined,
      division_ids: filters.divisions.length > 0 ? filters.divisions : undefined,
      type_values: filters.types.length > 0 ? filters.types : undefined,
      status_values: statusFilter.length > 0 ? statusFilter : undefined,
    };

    const { data: projects } = await apiClient.projectApi.getProjectsWithFilters(filterParams);

    for (const project of projects) {
      project.office_ids = project.office ? project.office.map((o) => o.id) : [];
      project.office_name = project.office ? project.office.map((o) => o.title).join(', ') : [];
      project.division_id = project.division ? project.division.id : null;
      project.division_name = project.division ? project.division.name : null;
    }
    commit('setProjectList', projects);
  },

  getAllProjectsForOptions: async ({ commit }) => {
    const { data: projects } = await apiClient.projectApi.getProjectsByViewer(undefined, undefined);

    for (const project of projects) {
      project.office_ids = project.office ? project.office.map((o) => o.id) : [];
      project.office_name = project.office ? project.office.map((o) => o.title).join(', ') : [];
      project.division_id = project.division ? project.division.id : null;
      project.division_name = project.division ? project.division.name : null;
    }
    commit('setAllProjectsForOptions', projects);
  },
};

/** @type {MutationTree<FilterProjectsState>} */
const mutations = {
  setNameFilter: (state, projectName) => (state.filters.projectName = projectName),
  setOfficesFilter: (state, offices) => (state.filters.offices = offices),
  setDivisionsFilter: (state, divisions) => (state.filters.divisions = divisions),
  setProjectList: (state, projectList) => (state.projectList = projectList),
  setAllProjectsForOptions: (state, allProjects) => (state.allProjectsForOptions = allProjects),
  setTypesFilter: (state, types) => (state.filters.types = types),
  setStatusesFilter: (state, statuses) => (state.filters.statuses = statuses),
  resetModuleState: (state) => Object.assign(state, getInitialState()),
};

/** @type {Module<FilterProjectsState, RootState>} */
export const filters = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
